import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale, useT } from '../../i18n/index.js';
import { LandingIcon } from '../../ui/LandingIcon.js';
import { claimHref, OFFER, offerEndsOn, rememberPromo } from './offer.js';

/**
 * The welcome offer, on a foil card the visitor rubs off.
 *
 * The gesture is the point, so it is a real one: a canvas with gold foil
 * painted on it and the prize sitting underneath in the DOM, cut away in
 * `destination-out` under the pointer. Three things decide whether that feels
 * like a scratch card or like a broken toy, and all three were wrong before:
 *
 *   - a stroke is a line, not the dots a pointer happens to report. Moving
 *     quickly left stripes of untouched foil between the dots, so the path is
 *     drawn from the last point to this one, with every coalesced point the
 *     browser held back;
 *   - measuring how much is gone reads the whole canvas back, which is far too
 *     expensive to do on every move. It now runs at most every fifth stroke
 *     and once more when the pointer lifts;
 *   - on a phone, a finger dragged across a canvas scrolls the page unless the
 *     element says otherwise. `touch-action: none` is what makes it scratch.
 *
 * Scratching is a pointer gesture and therefore not available to everyone, so
 * the same reveal is a plain button, and the prize is real text a screen
 * reader reads whether the foil is there or not. The canvas is decoration over
 * content, never the content itself.
 *
 * Everything the card says comes from `offer.ts`. There is no invented
 * urgency: the date shown is the one real end date, identical for every
 * visitor.
 */

/** Foil removed above this share, and the rest comes off for them. */
const CLEAR_AT = 0.5;
/** Sampled every Nth pixel: measuring all of them per stroke is not free. */
const SAMPLE_STRIDE = 4 * 32;
/** Strokes between two reads of the canvas. Reading it is the expensive part. */
const MEASURE_EVERY = 5;
/** Half the width of the scratched line, in CSS pixels. */
const NIB = 26;

export function ScratchCard({ onClose }: { onClose: () => void }) {
  const t = useT();
  const { locale, formatLocale } = useLocale();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const strokes = useRef(0);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const paintFoil = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const box = canvas.getBoundingClientRect();
    if (!box.width || !box.height) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(box.width * dpr);
    canvas.height = Math.round(box.height * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    // Gold, because foil is what a scratch card is made of and a card the
    // colour of the paper under it does not look like something to rub off.
    // One flat gold: the page carries no gradients, and the brushed strokes
    // below are what make it read as metal.
    ctx.fillStyle = '#d9ad48';
    ctx.fillRect(0, 0, box.width, box.height);

    // A few soft strokes across it, so the surface has a direction and the
    // flat gold reads as metal rather than as a coloured rectangle.
    ctx.save();
    ctx.globalAlpha = 0.07;
    ctx.strokeStyle = '#8a6412';
    ctx.lineWidth = 22;
    ctx.lineCap = 'round';
    for (let i = -1; i < 5; i += 1) {
      const x = box.width * (0.18 + i * 0.16);
      ctx.beginPath();
      ctx.moveTo(x, box.height * 0.18);
      ctx.lineTo(x - box.width * 0.14, box.height * 0.82);
      ctx.stroke();
    }
    ctx.restore();

    // The instruction belongs on the foil, not beside it: it has to go away as
    // the foil goes away, or it ends up printed over the prize.
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.82)';
    ctx.font = `600 ${Math.max(15, Math.min(21, box.width * 0.075))}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.letterSpacing = '0.14em';
    ctx.fillText(t('landing.offer.hint').toLocaleUpperCase(locale), box.width / 2, box.height / 2);
    ctx.restore();

    // Everything drawn after this cuts the foil away instead of adding to it.
    ctx.globalCompositeOperation = 'destination-out';
  }, [locale, t]);

  useEffect(() => {
    paintFoil();
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    // The card is fluid, and a canvas resized by CSS is a canvas stretched.
    const observer = new ResizeObserver(() => {
      if (!drawing.current && !revealed) paintFoil();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [paintFoil, revealed]);

  /*
   * Open means: the page underneath does not scroll, Escape closes, focus is
   * held inside, and whatever was focused before gets it back on the way out.
   */
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const dialog = dialogRef.current;
    dialog?.querySelector<HTMLElement>('button, a')?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const stops = dialog.querySelectorAll<HTMLElement>('button, a[href]');
      if (!stops.length) return;
      const first = stops[0]!;
      const end = stops[stops.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        end.focus();
      } else if (!event.shiftKey && document.activeElement === end) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      returnTo?.focus?.();
    };
  }, [onClose]);

  /** How much foil is gone, on a coarse sample rather than per pixel. */
  const scratchedShare = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return 0;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let clear = 0;
    let seen = 0;
    for (let i = 3; i < data.length; i += SAMPLE_STRIDE) {
      seen += 1;
      if (data[i]! < 32) clear += 1;
    }
    return seen ? clear / seen : 0;
  }, []);

  /** Rub the foil along the path the pointer actually took. */
  const scratchTo = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>, measure: boolean) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || revealed) return;
      const box = canvas.getBoundingClientRect();
      // A pointer at 120Hz reports in bursts; these are the points the browser
      // held back, and skipping them is what leaves gaps in a fast stroke.
      const points = (event.nativeEvent.getCoalescedEvents?.() ?? [event.nativeEvent]).map((e) => ({
        x: e.clientX - box.left,
        y: e.clientY - box.top,
      }));
      ctx.lineWidth = NIB * 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (const point of points) {
        const from = last.current ?? point;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
        // A single tap draws no line, so it gets a dot of its own.
        ctx.beginPath();
        ctx.arc(point.x, point.y, NIB, 0, Math.PI * 2);
        ctx.fill();
        last.current = point;
      }
      if (measure && scratchedShare() > CLEAR_AT) setRevealed(true);
    },
    [revealed, scratchedShare],
  );

  const copyCode = useCallback(() => {
    navigator.clipboard?.writeText(OFFER.code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => {
        // Denied clipboard permission is not an error worth a dialog: the code
        // is right there on screen to be read and typed.
      },
    );
  }, []);

  const stop = () => {
    drawing.current = false;
    last.current = null;
    if (!revealed && scratchedShare() > CLEAR_AT) setRevealed(true);
  };

  return (
    <div
      className="lp-scratch"
      // A click on the backdrop closes; a click that started inside the panel
      // does not, which is what `currentTarget` checks here.
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="lp-scratch-panel"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lp-scratch-title"
        data-revealed={revealed ? 'true' : undefined}
      >
        <button
          type="button"
          className="lp-scratch-dismiss"
          onClick={onClose}
          aria-label={t('landing.nav.close')}
        >
          <LandingIcon name="cross" />
        </button>

        <div className="lp-scratch-art" aria-hidden="true">
          <img
            src="/magix/heart.webp"
            alt=""
            width={384}
            height={384}
            decoding="async"
            fetchPriority="low"
          />
        </div>

        <div className="lp-scratch-body">
          <p className="lp-scratch-brand">Krevya</p>
          <h2 id="lp-scratch-title">{t('landing.offer.title')}</h2>
          <p className="lp-scratch-sub">{t('landing.offer.sub')}</p>

          <div className="lp-scratch-foil">
            <div className="lp-scratch-prize">
              <span>{t('landing.offer.lead')}</span>
              <strong>{t('landing.offer.pill', { percent: String(OFFER.percent) })}</strong>
            </div>
            <canvas
              ref={canvasRef}
              aria-hidden="true"
              onPointerDown={(event) => {
                drawing.current = true;
                strokes.current = 0;
                last.current = null;
                event.currentTarget.setPointerCapture(event.pointerId);
                scratchTo(event, false);
              }}
              onPointerMove={(event) => {
                if (!drawing.current) return;
                strokes.current += 1;
                scratchTo(event, strokes.current % MEASURE_EVERY === 0);
              }}
              onPointerUp={stop}
              onPointerCancel={stop}
            />
          </div>

          {revealed ? (
            <div className="lp-scratch-won">
              <button type="button" className="lp-scratch-code" onClick={copyCode}>
                <b>{OFFER.code}</b>
                <span>{copied ? t('landing.offer.copied') : t('landing.offer.copy')}</span>
              </button>
              <a className="lp-button" href={claimHref()} onClick={rememberPromo}>
                {t('landing.offer.use')}
                <LandingIcon name="arrow" />
              </a>
              {/* What the big number leaves out: which plan it applies to, and
                  until when. Both belong on screen, neither belongs in 40px. */}
              <p className="lp-scratch-ends">
                {t('landing.offer.prize', { percent: String(OFFER.percent) })} ·{' '}
                {t('landing.offer.ends', { date: offerEndsOn(formatLocale) })}
              </p>
            </div>
          ) : (
            <button type="button" className="lp-scratch-reveal" onClick={() => setRevealed(true)}>
              {t('landing.offer.reveal')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
